import React from "react";

import type { TField } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import * as Yup from "yup";

import { WorkDaysEditor } from "@/components/molecules/WorkDaysEditor/WorkDaysEditor";

import {
	getTranslatedDayName,
	orderedWorkDays,
	weeklyTotals,
} from "@/utils/UWorkDays";
import { cn } from "@/utils/cn";

import { TUserSettings } from "@/types";

const hoursLabel = (hoursPerDay: string | undefined) => {
	const [h, m] = (hoursPerDay || "00:00").split(":");
	return m && m !== "00" ? `${Number(h)} h ${m}` : `${Number(h)} h`;
};

const DayChips: React.FC<{ workDays: TUserSettings["workDays"] }> = ({
	workDays,
}) => (
	<span className="flex gap-1" data-testid="day-chips">
		{orderedWorkDays(workDays).map(([day, value]) => (
			<span
				key={day}
				title={`${getTranslatedDayName(day)}${
					value.isWorkingDay && value.hours ? ` · ${value.hours}` : ""
				}`}
				data-working={value.isWorkingDay}
				className={cn([
					"flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-medium",
					value.isWorkingDay
						? "bg-primary-500 text-white"
						: "bg-neutral-100 text-neutral-500",
				])}
			>
				{getTranslatedDayName(day).charAt(0).toUpperCase()}
			</span>
		))}
	</span>
);

const weeklyLine = (settings: TUserSettings) =>
	t("account.rhythm.weekly", weeklyTotals(settings));

export const RHYTHM_VALIDATION = () =>
	Yup.object().shape({
		hoursPerDay: Yup.string().required(t("forms.required")),
	});

/**
 * The rhythm card's fields, on `{ workDays, hoursPerDay }` - the
 * person's own (`GET/PUT /me/settings`). The daily rate is not here any
 * more: a rate is faced towards a client (see the project form).
 *
 * A function, not a constant: `t()` must run after
 * i18next.init(), and the days editor needs the form's current daily
 * default (amphore ships its own formik, so the app's useFormikContext is
 * empty inside a field renderer - the card passes it from the form ctx).
 * The days field hands its edit rendering to WorkDaysEditor (the same rows
 * the onboarding step has) and its display to the chips; the weekly line is
 * a display-only pseudo field.
 */
export const RHYTHM_FIELDS = (hoursPerDay?: string): TField[] => [
	{
		name: "workDays",
		type: "toggle",
		label: t("forms.rhythm.workingDays"),
		wrapperClassName: "col-span-full",
		renderer: (props) => {
			const { value, onChange } = props as unknown as {
				value: TUserSettings["workDays"];
				onChange: (value: TUserSettings["workDays"]) => void;
			};
			return (
				<WorkDaysEditor
					workDays={value}
					hoursPerDay={hoursPerDay}
					onChange={(day, dayValue) =>
						onChange({ ...value, [day]: dayValue })
					}
				/>
			);
		},
		valueDisplay: (values) => (
			<DayChips workDays={(values as TUserSettings).workDays} />
		),
	},
	{
		name: "hoursPerDay",
		type: "time",
		label: t("forms.rhythm.hoursPerDay"),
		required: true,
		valueDisplay: (values) =>
			hoursLabel((values as TUserSettings).hoursPerDay ?? undefined),
	},
	{
		name: "weekly",
		type: "input",
		hidden: (_values, isEditing) => isEditing,
		valueRenderer: (values) => (
			// The card orders its display items by position; a rendered
			// item has none, so it would come first without this.
			<p
				className="col-span-full text-xs text-neutral-500"
				style={{ order: 99 }}
			>
				{weeklyLine(values as TUserSettings)}
			</p>
		),
	},
];
